/* eslint-disable local-rules/function-naming */
type Next = () => Promise<void>;

type Attrs = {
	signal: AbortSignal;
};

export type MiddlewareAttrs = Prettify<
	Attrs & {
		index: number;
	}
>;

export type Middleware<T> = (context: T, next: Next, attrs: MiddlewareAttrs) => Promise<void>;

type MiddlewarePipeline<T> = {
	getStack: () => Middleware<T>[];
	push: (...middlewares: Middleware<T>[]) => void;
	run: (
		context: T,
		attrs?: Attrs,
	) => Promise<{
		done: boolean;
		meta: {
			lastIndex: number;
			lastRunMiddlewareName: string;
		};
	}>;
};

export function createMiddlewarePipeline<T>(...middlewares: Middleware<T>[]): MiddlewarePipeline<T> {
	const stack: Middleware<T>[] = middlewares;

	const push: MiddlewarePipeline<T>['push'] = (...extraMiddlewares) => {
		stack.push(...extraMiddlewares);
	};

	const run: MiddlewarePipeline<T>['run'] = async (context, attrs) => {
		let prevIndex = -1;

		const { signal }: Attrs = attrs ?? { signal: new AbortController().signal };

		const runner = async (index: number): Promise<void> => {
			if (signal?.aborted) {
				return;
			}

			if (index === prevIndex) {
				throw new Error('next() called multiple times');
			}

			prevIndex = index;

			const middleware = stack[index];

			if (middleware) {
				const next: Next = async () => {
					if (signal?.aborted) {
						return undefined;
					}

					return runner(index + 1);
				};

				await middleware(context, next, { index, signal });
			}
		};

		await runner(0);

		return {
			done: prevIndex === stack.length,
			meta: {
				lastIndex: prevIndex,
				lastRunMiddlewareName: stack[prevIndex]?.name,
			},
		};
	};

	const getStack = () => stack;

	return { getStack, push, run };
}
